import { router, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"
import { useRef } from "react"

interface ExpenseData {
  id?: number
  user_id: number | null
  expense_date: string | null
  expense_type: string
  expense_type_ref_id: number | null
  paid_to: string
  amount: number | string
  provider: string
  city: string
  state: string
  notes: string
  paid_by: string
  fuel_vehicle: string
  fuel_start_odometer: number
  fuel_end_odometer: number
  room_number: number | null
  stay_start: string | null
  stay_end: string | null
  rental_make: string
  rental_model: string
  rental_color: string
  rental_tag: string
  rental_tag_state: string
  air_flight1: string
  air_flight2: string
  air_destination: string
  receipt_status: string
  receipt?: File | null
  receipt_filename?: string | null
  receipt_content_type?: string | null
}

interface User {
  id: number
  name: string
}

interface ExpenseType {
  id: number
  name: string
}

interface Props {
  caseId: number
  expense: ExpenseData
  users: User[]
  expenseTypes: ExpenseType[]
  errors?: Record<string, string[]>
  onCancel: () => void
}

// Map ExpenseType names to sub-field sections
function typeName(types: ExpenseType[], id: number | null): string {
  if (!id) return ""
  return (types.find((t) => t.id === id)?.name ?? "").toLowerCase()
}

export default function ExpenseForm({
  caseId,
  expense,
  users,
  expenseTypes,
  errors,
  onCancel,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Filter out Per Diem (id=7) — that has its own form
  const filteredTypes = expenseTypes.filter((t) => t.name !== "Per Diem")

  const { data, setData, post, put, processing, transform } =
    useForm<ExpenseData>({
      user_id: expense.user_id,
      expense_date: expense.expense_date,
      expense_type: expense.expense_type,
      expense_type_ref_id: expense.expense_type_ref_id,
      paid_to: expense.paid_to,
      amount: expense.amount || "",
      provider: expense.provider,
      city: expense.city,
      state: expense.state,
      notes: expense.notes,
      paid_by: expense.paid_by,
      fuel_vehicle: expense.fuel_vehicle,
      fuel_start_odometer: expense.fuel_start_odometer,
      fuel_end_odometer: expense.fuel_end_odometer,
      room_number: expense.room_number,
      stay_start: expense.stay_start,
      stay_end: expense.stay_end,
      rental_make: expense.rental_make,
      rental_model: expense.rental_model,
      rental_color: expense.rental_color,
      rental_tag: expense.rental_tag,
      rental_tag_state: expense.rental_tag_state,
      air_flight1: expense.air_flight1,
      air_flight2: expense.air_flight2,
      air_destination: expense.air_destination,
      receipt_status: expense.receipt_status,
      receipt: null,
    })

  // Auto-sync expense_type string from the selected ExpenseType name
  transform((formData) => {
    const selectedType = expenseTypes.find(
      (t) => t.id === formData.expense_type_ref_id,
    )
    // Strip common currency noise ($, commas, whitespace) then parse.
    // On unparseable input, pass the raw string through so Rails numericality
    // validation rejects it instead of silently saving 0.
    const rawAmount = String(formData.amount ?? "").trim()
    const cleanedAmount = rawAmount.replace(/[$,\s]/g, "")
    const parsedAmount = cleanedAmount === "" ? null : Number(cleanedAmount)
    return {
      case_expense: {
        ...formData,
        expense_type: selectedType?.name ?? formData.expense_type,
        amount:
          parsedAmount === null || Number.isFinite(parsedAmount)
            ? parsedAmount
            : rawAmount,
      },
    }
  })

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    const options = { forceFormData: true }
    if (expense.id) {
      put(`/cases/${caseId}/expenses/${expense.id}`, options)
    } else {
      post(`/cases/${caseId}/expenses`, options)
    }
  }

  const handleRemoveReceipt = () => {
    if (expense.id && confirm("Remove the receipt file?")) {
      router.delete(`/cases/${caseId}/expenses/${expense.id}/remove_receipt`)
    }
  }

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value ? parseInt(e.target.value) : null
    setData("expense_type_ref_id", id)
  }

  const selectedTypeName = typeName(expenseTypes, data.expense_type_ref_id)
  const isFuel = selectedTypeName === "fuel"
  const isLodging = selectedTypeName === "lodging"
  const isRental = selectedTypeName === "vehicle rental"
  const isAirfare = selectedTypeName === "airfare"

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Basic Information</h3>

          <div>
            <label className={labelClass}>Employee</label>
            <select
              className={inputClass}
              value={data.user_id ?? ""}
              onChange={(e) =>
                setData(
                  "user_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
            >
              <option value="">Select employee...</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
            {errors?.user_id && (
              <p className={errorClass}>{errors.user_id[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Expense Date</label>
            <input
              type="date"
              className={inputClass}
              value={data.expense_date ?? ""}
              onChange={(e) => setData("expense_date", e.target.value)}
            />
            {errors?.expense_date && (
              <p className={errorClass}>{errors.expense_date[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Expense Type</label>
            <select
              className={inputClass}
              value={data.expense_type_ref_id ?? ""}
              onChange={handleTypeChange}
            >
              <option value="">Select type...</option>
              {filteredTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            {errors?.expense_type_ref_id && (
              <p className={errorClass}>{errors.expense_type_ref_id[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Paid To</label>
            <input
              type="text"
              className={inputClass}
              value={data.paid_to}
              onChange={(e) => setData("paid_to", e.target.value)}
              placeholder="e.g., Shell, Ruth Chris' Steakhouse, Parking Deck"
            />
          </div>
        </div>

        {/* Amounts */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Amounts</h3>

          <div>
            <label className={labelClass}>Amount</label>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]*"
              className={inputClass}
              value={data.amount}
              onChange={(e) => setData("amount", e.target.value)}
            />
            {errors?.amount && <p className={errorClass}>{errors.amount[0]}</p>}
          </div>

          <div>
            <label className={labelClass}>Paid By</label>
            <select
              className={inputClass}
              value={data.paid_by}
              onChange={(e) => setData("paid_by", e.target.value)}
              required
            >
              <option value="">--- SELECT ---</option>
              <option value="HPI AmEx">Company AmEx</option>
              <option value="Cash">Reimbursement Requested</option>
              <option value="Vendor">Vendor to Invoice</option>
            </select>
            {errors?.paid_by && (
              <p className={errorClass}>{errors.paid_by[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Receipt</label>
            {expense.receipt_filename && !data.receipt && (
              <div className="border-hpi-light-gray bg-hpi-pale-gray mt-1 mb-2 flex items-center gap-2 rounded border p-2 text-sm">
                <span className="text-hpi-dark-gray">
                  {expense.receipt_filename}
                </span>
                {expense.id && (
                  <button
                    type="button"
                    onClick={handleRemoveReceipt}
                    className="text-hpi-red text-xs hover:underline"
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="text-hpi-dark-gray file:bg-hpi-dark-blue hover:file:bg-hpi-blue mt-1 block w-full text-sm file:mr-4 file:rounded file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null
                setData("receipt", file)
                if (file) {
                  setData("receipt_status", "Y")
                }
              }}
            />
            <p className="text-hpi-med-gray mt-1 text-xs">
              PDF, JPEG, or PNG up to 10 MB
            </p>
            {errors?.receipt && (
              <p className={errorClass}>{errors.receipt[0]}</p>
            )}
          </div>
        </div>

        {/* Fuel Fields */}
        {isFuel && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Fuel Details</h3>
            <div>
              <label className={labelClass}>Vehicle</label>
              <input
                type="text"
                className={inputClass}
                value={data.fuel_vehicle}
                onChange={(e) => setData("fuel_vehicle", e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>Start Odometer</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputClass}
                value={data.fuel_start_odometer}
                onChange={(e) =>
                  setData(
                    "fuel_start_odometer",
                    parseFloat(e.target.value) || 0,
                  )
                }
              />
            </div>
            <div>
              <label className={labelClass}>End Odometer</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputClass}
                value={data.fuel_end_odometer}
                onChange={(e) =>
                  setData("fuel_end_odometer", parseFloat(e.target.value) || 0)
                }
              />
            </div>
          </div>
        )}

        {/* Lodging Fields */}
        {isLodging && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Lodging Details</h3>
            <div>
              <label className={labelClass}>Provider</label>
              <input
                type="text"
                className={inputClass}
                value={data.provider}
                onChange={(e) => setData("provider", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>City</label>
                <input
                  type="text"
                  className={inputClass}
                  value={data.city}
                  onChange={(e) => setData("city", e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>State</label>
                <input
                  type="text"
                  maxLength={2}
                  className={inputClass}
                  value={data.state}
                  onChange={(e) =>
                    setData("state", e.target.value.toUpperCase())
                  }
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Room Number</label>
              <input
                type="number"
                className={inputClass}
                value={data.room_number ?? ""}
                onChange={(e) =>
                  setData(
                    "room_number",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
              />
            </div>
          </div>
        )}

        {/* Rental Car Fields */}
        {isRental && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Rental Car Details</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Make</label>
                <input
                  type="text"
                  className={inputClass}
                  value={data.rental_make}
                  onChange={(e) => setData("rental_make", e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Model</label>
                <input
                  type="text"
                  className={inputClass}
                  value={data.rental_model}
                  onChange={(e) => setData("rental_model", e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Color</label>
              <input
                type="text"
                className={inputClass}
                value={data.rental_color}
                onChange={(e) => setData("rental_color", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>License Plate</label>
                <input
                  type="text"
                  className={inputClass}
                  value={data.rental_tag}
                  onChange={(e) =>
                    setData("rental_tag", e.target.value.toUpperCase())
                  }
                />
              </div>
              <div>
                <label className={labelClass}>State</label>
                <input
                  type="text"
                  maxLength={2}
                  className={inputClass}
                  value={data.rental_tag_state}
                  onChange={(e) =>
                    setData("rental_tag_state", e.target.value.toUpperCase())
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* Airfare Fields */}
        {isAirfare && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Airfare Details</h3>
            <div>
              <label className={labelClass}>Flight 1</label>
              <input
                type="text"
                className={inputClass}
                value={data.air_flight1}
                onChange={(e) =>
                  setData("air_flight1", e.target.value.toUpperCase())
                }
                placeholder="e.g., DL1234"
              />
            </div>
            <div>
              <label className={labelClass}>Flight 2</label>
              <input
                type="text"
                className={inputClass}
                value={data.air_flight2}
                onChange={(e) =>
                  setData("air_flight2", e.target.value.toUpperCase())
                }
                placeholder="e.g., DL5678"
              />
            </div>
            <div>
              <label className={labelClass}>Destination</label>
              <input
                type="text"
                className={inputClass}
                value={data.air_destination}
                onChange={(e) => setData("air_destination", e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <label className={labelClass}>Notes</label>
        <textarea
          className={inputClass}
          rows={3}
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {processing ? "Saving..." : expense.id ? "Update" : "Create"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
